/* eslint-disable no-restricted-globals */
import { isEmpty } from 'lodash';
import { ProductOption } from '../apis/models/products/res/ProductOption';
import { isAndroid, isIOS, getUA } from 'react-device-detect';
import { ReservationChannel } from '../apis/models/reservations/res/Reservation';
import moment from 'moment';
import { PartnerScheduleAvailableDate } from '../apis/models/partner-schedules/res/PartnerScheduleAvailableDate';
import strings from '../constants/strings.constants';
import { ProductFilter } from '../apis/models/products/res/ProductFilter';
import { LanguageCode } from '../store/useLanguageCodeStore';
import { Partner } from '../apis/models/partner/res/Partner';
import { Nation } from '../apis/models/nation/res/Nation';

import { PhoneNumberUtil } from 'google-libphonenumber';
import { ChatbotChannel } from '../apis/models/auth/req/ChatbotGuestSignInBody';

type ParsedPhoneNumber = {
  phoneNumber: string;
  nationalityNo: string | null;
  countryCode: string | null;
};

const phoneUtil = PhoneNumberUtil.getInstance();

export function parsePhoneNumber(rawInput: string): ParsedPhoneNumber {
  try {
    // '+'가 없으면 기본 국가를 KR로 가정
    const isInternational = rawInput.startsWith('+');
    const number = phoneUtil.parseAndKeepRawInput(
      rawInput,
      isInternational ? undefined : 'KR',
    );

    // 예외: 한국 번호인데 국제 포맷인 경우는 특별 처리
    const numericCountryCode = number.getCountryCode(); // e.g., 82
    const nationalNumber = number.getNationalNumber()?.toString(); // e.g., 1012345678
    const regionCode = phoneUtil.getRegionCodeForNumber(number); // e.g., KR, US

    if (numericCountryCode === 82 && nationalNumber?.startsWith('10')) {
      return {
        phoneNumber: '010' + nationalNumber?.slice(2),
        nationalityNo: null,
        countryCode: null,
      };
    }

    return {
      phoneNumber: nationalNumber || '',
      nationalityNo: numericCountryCode?.toString()
        ? `+${numericCountryCode?.toString()}`
        : null,
      countryCode: regionCode || null,
    };
  } catch (error) {
    return {
      phoneNumber: '',
      nationalityNo: null,
      countryCode: null,
    };
  }
}

export function convertToLocalPhoneNumber(phoneNumber: string): string | null {
  const validFormats = [
    /^010\d{8}$/, // 010xxxxxxxx (11자리)
    /^011\d{7}$/, // 011xxxxxxxx (10자리)
    /^02\d{7,8}$/, // 02xxxxxxxx (9~10자리)
    /^\+8210\d{8}$/, // +8210xxxxxxxx (11자리)
    /^\+8211\d{7}$/, // +8211xxxxxxxx (10자리)
  ];

  // 유효한 형식 중 하나라도 매칭되는지 확인
  const isValid = validFormats.some((format) => format.test(phoneNumber));
  if (!isValid) return null;

  return phoneNumber.replace(/\+?82/, '0');
}
export const getRecommendProductFilter = (
  recommendProductIds: number[],
): ProductFilter => ({
  id: -1,
  name: strings.aiCodeRecommendProductFilter,
  isEvent: false,
  isRecommendProduct: true,
  recommendProductIds,
});

export const getExhibitionProductFilter = (
  exhibitionId: number,
  eventId: number,
): ProductFilter => ({
  id: eventId,
  name: strings.exhibitionProductFilter,
  isEvent: true,
  exhibitionId,
});

export const getSelectedExhibitionProductFilter = (
  exhibitionId: number,
  eventId: number,
): ProductFilter => ({
  id: eventId,
  name: strings.exhibitionProductFilter,
  isEvent: true,
  selectedExhibitionId: exhibitionId,
});

export const getLanguageCode = (code: string): LanguageCode => {
  console.log('code', code);
  const filteredLangCode = Object.values(LanguageCode).filter((value) => {
    if (code.toLowerCase() === 'zh-tw') {
      return code.toLowerCase() === value;
    }

    return code.includes(value);
  });
  if (filteredLangCode.length > 0) {
    const [langCode] = filteredLangCode;
    return langCode;
  }
  return LanguageCode.ko;
};

export const getPartnerName = (partner?: Partner) => {
  if (partner) {
    let text = partner.displayName || partner.partnerName;
    if (partner.globalPartnerInfos?.length) {
      const info = partner.globalPartnerInfos[0];
      text = info.displayName || info.partnerName || text;
    }
    return text;
  }
  return '';
};

export const getPartnerFaviconUrl = (partner?: Partner) => {
  if (partner) {
    const url = `${process.env.REACT_APP_IMAGE_PRIFIX}homepage-meta/${partner.id}/favicon/favicon-96x96.png`;
    return url;
  }
  return '/favicon.ico';
};

export const getPartnerAddress = (partner?: Partner) => {
  if (partner) {
    let text = partner.street || '';
    if (partner.detail) {
      text += `${text ? ' ' : ''}${partner.detail}`;
    }
    if (partner.globalPartnerInfos?.length) {
      const info = partner.globalPartnerInfos[0];
      let infoText = info.street || '';
      if (info.detail) {
        infoText += `${infoText ? ' ' : ''}${info.detail}`;
      }
      text = infoText || text;
    }
    return text;
  }
  return '';
};

export const getNationOptions = (nations: Nation[]) => {
  return nations.map((n) => ({
    value: n.code, // KR, US, CA 등 (유니크)
    label: `${n.nationalityNo} ${NATION_CODE_TO_ENGLISH_NAME[n.code] || ''}`,
  }));
};

// code → nationalityNo 변환 (API 호출용)
export const getNationalityNoByCode = (
  nations: Nation[],
  code: string | null,
): string | undefined => {
  if (!code) return undefined;
  return nations.find((n) => n.code === code)?.nationalityNo;
};

export const getEarlyAvailableDateWhenDisabled = (
  date: Date,
  availDates: Record<string, PartnerScheduleAvailableDate>,
) => {
  const dateString = moment(date).format('YYYY-MM-DD');
  if (availDates[dateString]) {
    const workInfo = availDates[dateString];
    const { isWork } = workInfo;
    if (!isWork) {
      const enableDates = Object.keys(availDates).filter((key) => {
        return availDates[key].isWork;
      });

      if (enableDates) {
        const [enableDate] = enableDates;
        return moment(enableDate).toDate();
      }
    }
  }
};

export function isPhoneNum(num: string) {
  const regex = /^01([0|1|6|7|8|9])-?([0-9]{3,4})-?([0-9]{4})$/;
  return regex.test(num);
}

export function isEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function formatCurrency(num?: number) {
  if (!num) return '0';
  return num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function filterNumberString(string: string, max?: number) {
  const regex = /[^0-9]/g;
  let result = string.replace(regex, '');

  if (max) {
    const number = Number(result);
    if (number > max) {
      result = max.toString();
    }
  }

  return result;
}

export const formatTelNumber = (str: string) => {
  let result = str;
  if (isEmpty(str)) return '';
  if (str)
    if (str.length === 11)
      result = str.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
    else if (str.length === 8) result = str.replace(/(\d{4})(\d{4})/, '$1-$2');
    else if (str?.indexOf('02') === 0) {
      if (str?.length === 9) {
        result = str.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3');
      } else {
        result = str.replace(/(\d{2})(\d{4})(\d{4})/, '$1-$2-$3');
      }
    } else {
      result = str.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    }
  return result;
};

export const getProductOptionsPaymentPriceInfos = (
  productOptions: ProductOption[],
) => {
  let treatmentPrice = 0;
  let totalExpectPrice = 0;

  productOptions.forEach(
    ({ productOptionTotalDiscountPrice, productOptionTotalPrice }) => {
      let discountOptionPrice = productOptionTotalPrice;
      if (productOptionTotalDiscountPrice) {
        discountOptionPrice = productOptionTotalDiscountPrice;
      }
      if (productOptionTotalPrice) {
        treatmentPrice += productOptionTotalPrice;
      }
      if (discountOptionPrice) {
        totalExpectPrice += discountOptionPrice;
      }
    },
  );

  return {
    treatmentPrice,
    discountPrice: treatmentPrice - totalExpectPrice,
    totalExpectPrice,
  };
};

export const getProductOptionPrice = (productOption: ProductOption) => {
  const { productOptionTotalPrice, productOptionTotalDiscountPrice } =
    productOption;

  let price = productOptionTotalPrice;
  if (productOptionTotalDiscountPrice) {
    price = productOptionTotalDiscountPrice;
  }
  return price || 0;
};

export const getProductOptionTax = (productOption: ProductOption) => {
  const { productOptionTax, productOptionDiscountTax } = productOption;
  let tax = productOptionTax;
  if (productOptionDiscountTax) {
    tax = productOptionTax;
  }
  return tax;
};

export const getProductOptionTotalPrice = (productOptions: ProductOption[]) => {
  let total = 0;
  productOptions.forEach((productOption) => {
    total += getProductOptionPrice(productOption);
  });
  return total;
};

export const getProductOptionTotalTax = (productOptions: ProductOption[]) => {
  let total = 0;
  productOptions.forEach((productOption) => {
    total += getProductOptionTax(productOption);
  });
  return total;
};

export const isValidReservationChannel = (channel?: string) => {
  return (
    channel === ReservationChannel.CHANNEL_TALK_MESSENGER ||
    channel === ReservationChannel.WATCH_APP_MESSENGER ||
    channel === ReservationChannel.WECHAT_MESSENGER ||
    channel === ReservationChannel.LINE_MESSENGER ||
    channel === ReservationChannel.KAKAO_MESSENGER ||
    channel === ReservationChannel.NAVER ||
    channel === ReservationChannel.NAVER_MESSENGER
  );
};

export const isValidReservationPushChannel = (channel?: string) => {
  return (
    channel === ChatbotChannel.LINE ||
    channel === ChatbotChannel.RESPOND_IO ||
    channel === ChatbotChannel.WECHAT ||
    channel === ChatbotChannel.WHATS_APP
  );
};

export const closeWebPage = () => {
  const isKakaoInApp = getUA.match(/KAKAOTALK/i);
  const isLineInApp = getUA.match(/Line/i);

  if (isKakaoInApp) {
    if (isAndroid) {
      window.location.href = 'kakaotalk://inappbrowser/close';
    }
    if (isIOS) {
      window.location.href = 'kakaoweb://closeBrowser';
    }
  } else {
    // @ts-ignore
    window.open('', '_self').close();
    // let myWindow = open(location, "_self");
    // setTimeout(() => {
    //   if (myWindow) {
    //     myWindow.close();
    //   }
    // }, 500);
  }
};

export const NATION_CODE_TO_ENGLISH_NAME: { [key: string]: string } = {
  KR: 'South Korea',
  JP: 'Japan',
  CN: 'China',
  TH: 'Thailand',
  VN: 'Vietnam',
  TW: 'Taiwan',
  ID: 'Indonesia',
  MY: 'Malaysia',
  HK: 'Hong Kong',
  BN: 'Brunei',
  AU: 'Australia',
  DZ: 'Algeria',
  AR: 'Argentina',
  HR: 'Croatia',
  CO: 'Colombia',
  CA: 'Canada',
  AT: 'Austria',
  CY: 'Cyprus',
  BE: 'Belgium',
  CR: 'Costa Rica',
  AL: 'Albania',
  BR: 'Brazil',
  KH: 'Cambodia',
  CL: 'Chile',
  CZ: 'Czech Republic',
  QA: 'Qatar',
  SG: 'Singapore',
  KZ: 'Kazakhstan',
  RU: 'Russia',
  MO: 'Macau',
  IT: 'Italy',
  MA: 'Morocco',
  LT: 'Lithuania',
  RS: 'Serbia',
  LK: 'Sri Lanka',
  IN: 'India',
  KW: 'Kuwait',
  TN: 'Tunisia',
  PK: 'Pakistan',
  MG: 'Madagascar',
  ES: 'Spain',
  NZ: 'New Zealand',
  FJ: 'Fiji',
  EG: 'Egypt',
  CH: 'Switzerland',
  PY: 'Paraguay',
  NI: 'Nicaragua',
  UA: 'Ukraine',
  KG: 'Kyrgyzstan',
  IE: 'Ireland',
  LU: 'Luxembourg',
  EC: 'Ecuador',
  TJ: 'Tajikistan',
  PA: 'Panama',
  AE: 'United Arab Emirates',
  ZA: 'South Africa',
  GH: 'Ghana',
  PH: 'Philippines',
  MM: 'Myanmar',
  DO: 'Dominican Republic',
  DK: 'Denmark',
  IL: 'Israel',
  GT: 'Guatemala',
  UY: 'Uruguay',
  MN: 'Mongolia',
  IR: 'Iran',
  SD: 'Sudan',
  GR: 'Greece',
  SA: 'Saudi Arabia',
  VE: 'Venezuela',
  GB: 'United Kingdom',
  NP: 'Nepal',
  SY: 'Syria',
  NO: 'Norway',
  EE: 'Estonia',
  NG: 'Nigeria',
  ET: 'Ethiopia',
  PE: 'Peru',
  TM: 'Turkmenistan',
  PT: 'Portugal',
  UZ: 'Uzbekistan',
  MU: 'Mauritius',
  MX: 'Mexico',
  FI: 'Finland',
  SI: 'Slovenia',
  TR: 'Turkey',
  PL: 'Poland',
  NL: 'Netherlands',
  KE: 'Kenya',
  FR: 'France',
  RO: 'Romania',
  SE: 'Sweden',
  DE: 'Germany',
  US: 'United States',
  OM: 'Oman',
  BY: 'Belarus',
  BD: 'Bangladesh',
  AZ: 'Azerbaijan',
  BT: 'Bhutan',
  HU: 'Hungary',
  BZ: 'Belize',
  IQ: 'Iraq',
  GE: 'Georgia',
};
